import { Button } from '../Button.tsx';
import { Icon, type IconName } from '../../shell/Icon.tsx';
import { duration, utcTime } from '../format.ts';
import { faultTone, stateWord, type FaultClass } from './fault.ts';
import { useOnline, type Outage, type Read } from './read.ts';
import { endpointCounts, type Source } from './sources.ts';
import styles from './states.module.css';

/* ==========================================================================
   A read that failed, said ONCE.

   The console used to say it many times. On a cold start against a dead API a
   1600px frame carried six Retry buttons, eight `no reply` chips and four
   different sentences for one event, and an operator scanning it had to work
   out which of the six verbs was the real one before they could work out what
   had broken. Noise, at exactly the moment noise costs the most.

   So the failure is split into two pieces that never overlap:

     THE BANNER   one line under the surface header. The verdict, the one
                  sentence, the machine detail, the countdown, and the ONE
                  recovery on the whole surface.
     THE LINE     one --error line inside each affected panel, at the position
                  of that panel's first row. It carries the ONE thing that is
                  this panel's alone — which source it was waiting on — and
                  never a sentence, never a verb, never a second diagnosis.

   Between them the operator gets the diagnosis once, the recovery once, and
   per-panel scope everywhere it matters.
   ========================================================================== */

const GLYPH: Record<FaultClass, IconName> = {
  link: 'link-off',
  timeout: 'clock',
  server: 'alert-triangle',
  rate: 'clock',
  auth: 'lock',
  forbidden: 'lock',
  missing: 'x-circle',
  rejected: 'alert-octagon',
  malformed: 'alert-triangle',
  unknown: 'alert-triangle',
};

/* --------------------------------------------------------------------------
   FaultLine — one --error line where the panel's first row would be.

   Not a bar with a ground, not a block, not a card. A line, on the row grid,
   at the row pitch, in the panel's own left inset — the same shape the empty
   state uses, so a panel with nothing in it and a panel that cannot be read
   differ by their words and their colour and by nothing else.
   -------------------------------------------------------------------------- */

export function FaultLine({
  read,
  /**
   * Print how old the figures under this line are. False only where the
   * panel's header tag already prints it.
   */
  age = true,
}: {
  read: Read;
  age?: boolean | undefined;
}) {
  const fault = read.fault;
  if (fault === null) return null;

  /* THE STATE FIRST, IN THE TONE; THE MACHINE DETAIL AFTER, IN GREY.

     An operator scanning six panels is looking for one thing — which of them
     have stopped — and the endpoint is what they read second, once they have
     found one. Leading with `/api/robots` puts eight characters of shared
     prefix in front of the only word that differs. */
  const bits: string[] = [];
  if (read.path !== null) bits.push(read.path);
  if (fault.status !== null) bits.push(String(fault.status));
  if (read.attempts > 1) bits.push(`${read.attempts} lost`);
  /* A panel that has never landed has no age at all — so the line stamps the
     last read this SESSION saw, in wall time, rather than an elapsed figure
     that would be measured from nothing. A panel that HAS landed gets the
     elapsed form below, because there the question is not "when" but "how far
     behind is what I am looking at". */
  if (!read.hasData && read.lastGoodIso !== null) {
    bits.push(`last good ${utcTime(read.lastGoodIso)}`);
  } else if (age && read.hasData && read.ageMs !== null) {
    /* THE HALF OF THE STALE STATE THE CONSOLE WAS NOT SAYING.

       A stalled panel keeps every figure it was holding, at full strength —
       an operator mid-incident needs the last known fleet more than they need
       a blank screen, and more than they need a grey one. But every relative
       age INSIDE those figures (`Live 34s`, `18h ago`, a `2m` time-in-state)
       was computed when the read landed and has been frozen ever since. Under
       a 40-second outage the list says `34s` about something that happened 74
       seconds ago, and nothing on the row admits it.

       This is also the reason the rows themselves are not dimmed: the thing
       that is wrong with them is not their legibility, it is their DATE, and
       a date is a fact you print, not a fact you imply with alpha.

       So the line that says the read stopped also says how far behind the
       rows beneath it now are, and the operator adds the two. It is stated in
       the same mono, on the same line, next to the reason — not as a second
       piece of chrome — because it is a property of THIS read and it is only
       true while this line is on screen.

       Suppressed by `age={false}` where a `ReadTag` in the panel's own header
       is already carrying it (see ReadPanel): the same number twice, 24px
       apart, is how a panel stops being read at all. */
    bits.push(`figures ${duration(read.ageMs)} old`);
  }

  return (
    /* NOT a live region. The run count in it increments on every poll, so a
       screen reader would re-read six fault lines every five seconds for as
       long as the outage lasted. The banner's verdict is announced once
       (role="alert" on the word, which changes only when the verdict does) and
       these lines are read where they sit, with the panel they belong to. */
    /* The 2px leading edge — THE BAR §5's third rung, and the one that scales:
       the same mark the exception rows and the queue cards wear, so a stopped
       panel is found by the same scan. It is the line's own border-left rather
       than a child span, which is why there is no tick element here. */
    <div className={styles.faultLine} data-tone={faultTone(fault.class)}>
      <Icon name={GLYPH[fault.class]} size={12} />
      <span className={styles.faultWord}>{stateWord(fault.class)}</span>
      {bits.length > 0 && <span className={styles.faultText}>{bits.join('  ·  ')}</span>}
    </div>
  );
}

/* --------------------------------------------------------------------------
   OutageBanner — the one statement, and the one verb.

   Three verdicts, because the operator's next action really is different in
   each, and the distinction is the whole reason this piece exists:

     NO NETWORK       this machine is not on a network, and reads are really
                      failing because of it. Fix the laptop; nothing beyond it
                      is even reachable, let alone broken.
     LINK DOWN        the machine is on a network and requests are still not
                      arriving. The VPN, the site link or the API. Not yours.
     NOT ANSWERING    they arrive and the API refuses every one. Call on-call.
     PARTIAL OUTAGE   some endpoints are out and the rest are fine. Carry on
                      working; this list is what you cannot trust.

   The first two used to be one word. They are the two halves of "you are
   disconnected" and they send the operator to two different places, so
   collapsing them costs the first minute of every incident: `LINK DOWN` over
   a dropped wi-fi association reads as an outage and gets escalated as one.

   And one more that is not an outage at all — SESSION EXPIRED — because a 401
   answered promptly by a perfectly healthy API is not a supply failure and
   Retry cannot fix it.
   -------------------------------------------------------------------------- */

export function OutageBanner({
  outage,
  sources,
  onRetry,
  onSignIn,
}: {
  outage: Outage;
  sources: readonly Source[];
  onRetry?: (() => void) | undefined;
  onSignIn?: (() => void) | undefined;
}) {
  /* Hooks before the early return: the banner unmounts and remounts as the
     fleet recovers, and a conditional subscription would be a hook-order
     violation the first time it did. */
  const online = useOnline();
  const failing = sources.filter((s) => s.supply);
  if (failing.length === 0) return null;

  /* WHAT KIND OF FAILURE, IN ONE WORD.

     Four verdicts, because the operator's next action really is different in
     each — and they are decided off the CLASSES the sources actually carry,
     not off a count, so a console being throttled never wears the same red
     word as a console whose site link has gone. */
  const classes = new Set(failing.map((s) => s.cls));
  const only = classes.size === 1 ? [...classes][0] : null;
  const expired = only === 'auth';
  const throttled = only === 'rate';
  /* An expired session is the ONE fault Retry cannot touch, so it gets the one
     verb that can. Where the deployment has no sign-in of its own (IAP, and
     local dev) a full reload is the honest equivalent: it re-runs the identity
     handshake from the top. A disabled button with no reason would be worse
     than either. */
  const signIn = onSignIn ?? (() => window.location.reload());
  /* Amber where every failing source is a fault the platform is answering
     promptly and correctly — an expired credential, a throttle — and red
     where something is actually broken. The banner and every panel line ask
     the same function, so they cannot disagree. */
  const tone = failing.every((s) => s.cls !== null && faultTone(s.cls) === 'warn')
    ? 'warn'
    : 'error';
  /* Endpoints, not query instances — see `endpointCounts`. The banner, the
     supply line and the ledger's eyebrow all print this same fraction. */
  const counts = endpointCounts(sources);

  /* THE BROWSER'S FLAG EARNS A WORD ONLY WHEN A REAL READ HAS FAILED FOR IT.

     `navigator.onLine` alone is a claim about an interface, and this console
     is deliberately built not to trust it (see `useOnline`, and decision 2 in
     queryClient.ts). Paired with `outage.down > 0` it is no longer a claim: at
     least one request has genuinely failed at the transport layer while the OS
     says there is no network to carry it, and that conjunction has exactly one
     reading. It outranks `systemic` and `partial` because a machine off the
     network cannot be diagnosing anything further out than itself. */
  const offline = !online && outage.down > 0;

  const word = expired
    ? 'Session expired'
    : throttled
      ? 'Rate limited'
      : offline
        ? 'No network'
        : outage.disconnected
          ? 'Link down'
          : outage.systemic
            ? 'Not answering'
            : 'Partial outage';

  /* THE ONE SENTENCE CARRIES THE AGE WHEN THERE ARE FIGURES TO AGE.

     THE BAR §11.10: "never the last known value without an age beside it."
     Per value is the panel's job and this console owes it panel by panel — but
     when the WHOLE surface has stopped being supplied, every figure on it is
     exactly as old as the last thing that arrived, and that is one fact, once,
     in the one line the operator is already reading. `Nothing is reaching the
     API` is a diagnosis; `Nothing has reached this console for 1m 45s` is the
     provenance of everything under it, which is what they actually need before
     they act on a number.

     Only where something HAS landed this session — with a cold console there
     is no age to state and nothing under the banner to qualify, so the
     diagnosis stands on its own. */
  /* THE SENTENCE A SCREEN READER GETS, AND WHY IT IS NOT THE ONE ON SCREEN.

     The visible sentence carries the silence (`Nothing has reached this
     console for 27s.`) and that number ticks once a second. Put `role="alert"`
     on it and assistive tech re-reads the whole verdict every second for as
     long as the incident lasts, which is not an announcement, it is a siren
     nobody can turn off. Put it on the two-word verdict alone — which is what
     this banner did — and the announcement is `Link down` with no object and
     no consequence.

     So the alert is its own visually-hidden node carrying the verdict AND the
     stable half of the sentence. It changes when the DIAGNOSIS changes and at
     no other time, so it is announced once per state, which is exactly how
     often an operator wants to hear it. */
  const stable = expired
    ? 'This console is no longer signed in.'
    : throttled
      ? 'The API is throttling this console.'
      : offline
        ? 'This machine is off the network.'
        : outage.disconnected
          ? 'Nothing is reaching the API.'
          : outage.systemic
            ? 'The API is answering every read with a fault.'
            : `${counts.failing} of ${counts.total} sources ${counts.failing === 1 ? 'has' : 'have'} stopped answering.`;

  const silence = outage.sinceMs === null ? null : duration(outage.sinceMs);
  const sentence = expired
    ? 'This console is no longer signed in.'
    : throttled
      ? 'The API is throttling this console.'
      : offline
        ? silence === null
          ? 'This machine is off the network — nothing beyond it has been reached.'
          : `This machine is off the network. Nothing has reached the console for ${silence}.`
        : outage.disconnected
          ? silence === null
            ? 'Nothing is reaching the API.'
            : `Nothing has reached this console for ${silence}.`
          : outage.systemic
            ? silence === null
              ? 'The API is answering every read with a fault.'
              : `The API is answering every read with a fault. Nothing since ${silence} ago.`
            : `${counts.failing} of ${counts.total} sources ${counts.failing === 1 ? 'has' : 'have'} stopped answering.`;

  /* The mono half: which sources, how long the screen has been a memory, and
     when the console will try again — the three facts the sentence above
     deliberately does not carry, so the sentence stays one short line. */
  const named = counts.names.slice(0, 3);
  if (counts.names.length > named.length) named.push(`+${counts.names.length - named.length}`);

  const detail: string[] = [named.join(' · ')];
  /* THE GAP IS THE FAILING SOURCES' GAP, NOT THE CONSOLE'S.

     `outage.sinceMs` is the age of the newest good read ANYWHERE, which during
     a partial outage is a source that is working perfectly — so the banner
     used to report `last read 0s ago` over an endpoint that had been dead for
     four minutes. The figure an operator needs is the longest silence among
     the sources that stopped. */
  const never = failing.filter((s) => s.lastGoodIso === null).length;
  const worst = failing.reduce<number | null>(
    (max, s) => (s.ageMs === null ? max : max === null ? s.ageMs : Math.max(max, s.ageMs)),
    null,
  );
  /* The sentence above already carries the silence whenever the whole surface
     has stopped, so the detail states it only where the sentence does not —
     a partial outage, where the console-wide age is a source that is working
     perfectly and the figure an operator needs is the longest silence among
     the ones that stopped. */
  const stated = (offline || outage.disconnected || outage.systemic) && silence !== null;
  if (worst === null) detail.push('nothing has landed this session');
  else if (never > 0) detail.push(`silent ${duration(worst)} · ${never} never read`);
  else if (!stated) detail.push(`silent ${duration(worst)}`);

  const soonest = failing.reduce<number | null>(
    (best, s) => (s.nextInMs === null ? best : best === null ? s.nextInMs : Math.min(best, s.nextInMs)),
    null,
  );
  const reading = failing.some((s) => s.fetching);
  /* NOTHING FURTHER WILL HAPPEN UNLESS A HUMAN ASKS.

     Read off the sources rather than off this instant: a source is dormant
     when its retry ladder is spent AND it carries no cadence, which is a fact
     about the policy and does not flicker. `soonest === null` alone is an
     instant — it is null for the whole of every in-flight fetch — and a verb
     that appears and disappears twice a second is a verb nobody can press. */
  const dormant = failing.every((s) => s.nextInMs === null && s.everyMs === null && !s.fetching);
  if (reading) detail.push('reading…');
  /* A COUNTDOWN NEVER READS ZERO — AND IT NEVER READS AS A MISSING VALUE.

     `soonest` is sampled on a 1 Hz tick, so the last frame before a retry
     fires lands anywhere in [0, 1s) and printed `next attempt 0s` — a
     countdown that has arrived and a screen that has not moved, which reads as
     a console that has stopped trying.

     It is also the COMMON case rather than the edge one, and that is what made
     the first phrasing (`next attempt due`) wrong. `soonest` is the minimum
     over every failing source, and with four to eight of them each counting
     down its own independent ladder the minimum is under a second most of the
     time — so on a cold outage the line ended `· next attempt due` on all six
     routes, which reads as a label whose value failed to render, sitting in a
     mono run where every other item is `label value`.

     `<1s` is the same fact in the shape the rest of the line has: a bound, in
     the same ink, that can never be mistaken for a value that did not
     arrive. */
  else if (soonest !== null) {
    detail.push(soonest < 1_000 ? 'next attempt <1s' : `next attempt ${duration(soonest)}`);
  } else if (!expired && dormant) detail.push('no further attempt');

  /* THE ONE VERB THAT MAKES THINGS WORSE.

     `Retry` is the right offer for four of the five verdicts: a dropped link,
     a timeout and a 502/503/504 all clear on their own, and pressing it costs
     one request that was going to happen anyway. Under a 429 it is the exact
     opposite — the API has said "stop", the console's own ladder has already
     backed off to 2 s, and the button invites an operator to hammer the
     service that is throttling them until the window gets longer.

     So while the console is still going to try on its own, the banner offers
     no verb at all and shows the countdown instead: it is already doing the
     only useful thing, and saying so is the recovery. The moment it is
     dormant the position inverts — nothing further will happen unless a human
     asks — and the verb comes back, because a screen that has given up and
     does not say how to un-give-up is worse than one that hammers. */
  const holdOff = throttled && !dormant;

  return (
    <div className={styles.banner} data-tone={tone}>
      <span className={styles.tick} />
      <span className={styles.bannerIcon}>
        <Icon
          /* `link-off` for both halves of "you are disconnected": the two
             verdicts differ in WHERE the break is, not in whether there is
             one, and giving them separate glyphs would ask an operator to
             learn a second symbol for the same picture. The word beside it is
             what forks. */
          name={
            expired
              ? 'lock'
              : throttled
                ? 'clock'
                : offline || outage.disconnected
                  ? 'link-off'
                  : 'alert-triangle'
          }
          size={13}
        />
      </span>
      {/* The live region is the verdict and the sentence, and stops before the
          detail: the countdown in it reticks every second and a region that
          contained it would have a screen reader announce the outage sixty
          times a minute. */}
      <span className={styles.sr} role="alert">
        {word}. {stable}
      </span>
      {/* Hidden from the accessibility tree because the alert above IS these
          two nodes, said once. Leaving both readable puts the verdict in the
          tree twice — once as an announcement and once as content — and an
          operator arrowing through the banner hears "Link down, nothing is
          reaching the API, link down, nothing is reaching the API". The mono
          detail and the verb after them stay readable: they are the part the
          alert deliberately does not carry. */}
      <span className={styles.bannerWord} aria-hidden="true">
        {word}
      </span>
      <span className={styles.headline} aria-hidden="true">
        {sentence}
      </span>
      <span className={styles.detail}>{detail.join('  ·  ')}</span>
      <span className={styles.spacer} />
      {/* THE ONE RECOVERY ON THE SURFACE, AND IT IS NOT THE SURFACE'S PRIMARY.

          Outlined, not filled. THE BAR §6 allows exactly one filled `--accent`
          verb per surface and the route already owns it — `New robot` on the
          register, `Publish` on the catalog, `Deregister` on a robot — so a
          filled Retry in the banner puts two of them on one 1600px frame and
          the operator has to work out which is the primary. Outlined is also
          the honest weight: this is the way back to a working screen, not the
          screen's purpose. It is still the only verb the outage chrome
          offers, which is what makes it findable. */}
      {expired ? (
        <Button size="sm" variant="secondary" icon="lock" onClick={signIn}>
          Sign in again
        </Button>
      ) : (
        onRetry !== undefined &&
        !holdOff && (
          /* NOT DISABLED WHILE A READ IS IN FLIGHT, AND THIS WAS A BUG.

             `disabled={reading}` looked like courtesy — do not let them press
             it twice — and against four sources on a 3–5 s poll it made the
             surface's ONLY recovery verb dead about four fifths of the time,
             flickering on and off twice a second. Worse, a disabled button
             leaves the tab order, so during a total outage the one control an
             operator most wants was not reachable from the keyboard at all.

             There is nothing to protect against: `refetchQueries` folds into
             the fetch that is already running, so a press mid-read costs
             nothing. The banner's own `reading…` says work is in flight, which
             is what the disabled state was trying to communicate and does far
             better, because it does not move. */
          <Button size="sm" variant="secondary" icon="refresh" onClick={onRetry}>
            Retry
          </Button>
        )
      )}
    </div>
  );
}
